import React from "react";
import PropTypes from "prop-types";
import TextField from "../forms/TextField";
import { FormattedMessage } from "react-intl";
import Button from "../Button";
import PasswordInput from "react-password-indicator";
import isEmail from "validator/lib/isEmail";
import Loader from "../Loader";
import { injectIntl } from "react-intl";

class SignupForm extends React.Component {
    static propTypes = {
        email: PropTypes.string,
        emailExist: PropTypes.bool,
        password: PropTypes.string,
        domain: PropTypes.string,
        handleFormSubmit: PropTypes.func,
        onTextFieldChange: PropTypes.func,
        children: PropTypes.func,
        isLoading: PropTypes.bool,
    };

    state = {
        emailValidation: { errors: [], valid: true },
        passwordValidation: {},
    };

    static getDerivedStateFromProps(nextProps) {
        if (nextProps.emailExist) {
            return {
                emailValidation: {
                    errors: [
                        {
                            key: "emailExist",
                            message: <FormattedMessage id="error.emailExists" />,
                        },
                    ],
                    valid: false,
                },
            };
        }
        if (nextProps.email == "") {
            return {
                emailValidation: { errors: [], valid: true },
            };
        }
        return null;
    }

    onEmailBlur = () => {
        if (this.props.email) {
            let emailValidation = { errors: [], valid: true };
            if (!isEmail(this.props.email)) {
                emailValidation = {
                    errors: [
                        {
                            key: "noValidFormat",
                            message: <FormattedMessage id="error.invalidEmail" />,
                        },
                    ],
                    valid: false,
                };
            } else {
                // we don't allow signups with '+' in the email address
                if (this.props.email.indexOf("+") > -1) {
                    emailValidation = {
                        errors: [
                            {
                                key: "containsPlus",
                                message: <FormattedMessage id="error.emailContainsPlus" />,
                            },
                        ],
                        valid: false,
                    };
                } else if (
                    this.props.domain &&
                    this.props.domain !== this.props.email.split("@")[1]
                ) {
                    emailValidation = {
                        errors: [
                            {
                                key: "noValidDomain",
                                message: (
                                    <FormattedMessage
                                        id="error.invalidDomain"
                                        values={{
                                            domain: <em>{this.props.domain}</em>,
                                        }}
                                    />
                                ),
                            },
                        ],
                        valid: false,
                    };
                }
            }
            this.setState({
                emailValidation: emailValidation,
            });
        }
    };
    render() {
        let formatMessage = this.props.intl.formatMessage;
        // Custom password error messages
        const passwordErrorMessages = {
            minLen: formatMessage({ id: "error.password.tooShort" }),
            digits: formatMessage({ id: "error.password.oneDigit" }),
            specialChars: formatMessage({
                id: "error.password.oneSpecialChar",
            }),
            uppercaseChars: formatMessage({
                id: "error.password.oneUppercase",
            }),
        };

        let buttonContent = this.props.isLoading ? (
            <Loader white size="xsmall" />
        ) : (
            <FormattedMessage id="signup.forms.signupBtn" defaultMessage="Submit" />
        );

        return (
            <div className="signupForm">
                <form
                    onSubmit={e => {
                        e.preventDefault();
                        if (
                            this.state.emailValidation.valid &&
                            this.state.passwordValidation.valid
                        ) {
                            this.props.handleFormSubmit(e);
                        }
                    }}
                >
                    <TextField
                        className={this.state.emailValidation.valid ? "valid" : "invalid"}
                        type="text"
                        label={<FormattedMessage id="signup.forms.emailField" />}
                        placeholder={
                            this.props.domain
                                ? `your@${this.props.domain}`
                                : formatMessage({
                                      id: "signup.forms.emailPlaceholder",
                                  })
                        }
                        type="text"
                        id="email"
                        name="email"
                        onChange={this.props.onTextFieldChange}
                        onBlur={this.onEmailBlur}
                        value={this.props.email}
                        tabIndex={1}
                        autoFocus={true}
                    />
                    <div
                        className={`input-status ${
                            this.state.emailValidation.valid ? "valid" : "invalid"
                        }`}
                    >
                        {this.state.emailValidation.valid ? (
                            this.props.domain && (
                                <ul>
                                    <li className="input-hint">
                                        <FormattedMessage
                                            id="signup.hint.domain"
                                            values={{
                                                domain: <em>{this.props.domain}</em>,
                                            }}
                                        />
                                    </li>
                                </ul>
                            )
                        ) : (
                            <ul>
                                {this.state.emailValidation.errors.map(e => (
                                    <li key={e.key}>{e.message}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <PasswordInput
                        minLen={6} // minimum length 6
                        digits={1} // at least 1 digit
                        specialChars={1} // at least 1 special char
                        uppercaseChars={1} // at least 1 uppercase char
                        defaultMessages={passwordErrorMessages}
                        value={this.props.password}
                        onChange={(value, validation) =>
                            this.setState({
                                passwordValidation: validation,
                            })
                        }
                    >
                        {({ getInputProps, touched, valid, errors }) => (
                            <React.Fragment>
                                <TextField
                                    {...getInputProps()}
                                    className={touched ? (valid ? "valid" : "invalid") : ""}
                                    value={this.props.password}
                                    type="password"
                                    label={<FormattedMessage id="signup.forms.passwordField" />}
                                    id="password"
                                    name="password"
                                    onChange={this.props.onTextFieldChange}
                                    tabIndex={2}
                                />
                                <div className={`input-status ${valid ? "valid" : "invalid"}`}>
                                    {this.state.passwordValidation.errors && (
                                        <ul>
                                            {this.state.passwordValidation.errors.map(e => (
                                                <li key={e.key}>{e.message}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </React.Fragment>
                        )}
                    </PasswordInput>
                    <div className="button">
                        <Button tabIndex={3}>{buttonContent}</Button>
                    </div>
                </form>
            </div>
        );
    }
}

export default injectIntl(SignupForm);
